<template>
    <section id="orderDetails">
        <!-- ===== PER-QUOTE PRINT-ONLY HEADERS =====
             The letterhead (logo + Parts Department address)
             used to live here as a single page-level banner.
             With multi-quote print support, each Quote # group
             now carries its own copy of the header inside the
             v-for("group in groupedOrders") loop further down,
             and a `page-break-before: always` rule on
             subsequent groups gives every quote its own page
             in print. The per-quote header markup is identical
             to what used to render here, just relocated. See
             the `.print-only-header` block inside the v-for
             and the @media print rules at the bottom of this
             file. -->

        <!-- The screen-rendered logo error fallback flag is
             consumed by every per-quote header instance below
             (single piece of state, multiple consumers). -->

        <div id="breadcrumb">
            <div class="container">
                <ul>
                    <li><router-link to="/" tag="a">Home</router-link></li>
                    <li><router-link to="/account/rfqhistory" tag="a">Quote History</router-link></li>
                    <li class="last">Quote Details</li>
                </ul>
            </div>
        </div>

        <div id="odTop">
            <div class="container">
                <!-- ===== PAGE HEADER =====
                     Quote # is the headline; Date sits beneath it as
                     supporting text. Action buttons (Convert to Order,
                     Delete Quote) live in the top-right of this row,
                     visible from the moment the page loads — no need
                     to scroll to act on the quote. Disclaimer for the
                     conversion action sits beneath the buttons, small
                     and italic. No status pill — quotes don't carry
                     a shipping status the way orders do. -->
                <div class="order-page-header">
                    <div class="order-page-header-main">
                        <h1>Quote #{{id}}</h1>
                        <p class="order-page-header-meta">Quote Date: {{orderDate | formatDate}}</p>
                    </div>
                    <div class="order-page-header-actions no-print"
                         v-if="orderDetails && orderDetails.length > 0">
                        <div class="header-action-buttons">
                            <a @click="convertToOrder(id)" class="btn btn-primary btn-sm">Convert to Order</a>
                            <a @click="showDeleteModal(id)" class="btn btn-dark btn-sm">Delete Quote</a>
                        </div>
                        <p class="header-action-disclaimer">
                            Pricing and availability are subject to change.
                        </p>
                    </div>
                </div>

                <!-- ===== SPECIAL INSTRUCTIONS =====
                     Only renders if instructions were attached. Quoted-
                     block treatment so user text reads as a recorded
                     note rather than continuation of page prose. -->
                <div class="special-instructions-block" v-if="specialInstructions">
                    <div class="special-instructions-label">Special Instructions</div>
                    <div class="special-instructions-text">{{ specialInstructions }}</div>
                </div>

        <!-- NOTE: #odTop > .container is intentionally left OPEN here so the
             ENTIRE left content (page header + action buttons, Special
             Instructions, Print band, and the grouped Quote-Reference/assembly
             blocks) renders inside ONE standard content card — the shared
             brand-stripe card drawn by `#orderDetails #odTop > .container::before`
             in steele-consistency-fix.css (RFQDetails also roots at
             #orderDetails). This matches the single-card layout the Cart page
             uses. The inner sections below DROP their own `.container` wrappers
             (which would otherwise each become a nested white card via the
             `#orderDetails .container` panel rule) — they're plain <div>s now.
             The container + #odTop are closed just before the modal further
             down. -->

        <!-- ===== PRINT QUOTE ACTION =====
             Centered Print button in its own breathing-room band
             between the page header and the quote detail blocks.
             Opens the Print Quote(s) modal — see #print-quote-modal
             below. The modal lets the user confirm the action and,
             when more than one quote is present, pick which subset
             to print. The modal's confirm handler is what actually
             calls window.print() (the user picks "Save as PDF" from
             the destination dropdown if they want a PDF). The
             @media print stylesheet hides the site chrome, the
             action buttons, and this section itself so the
             printout is just the receipt content matching the
             on-screen page. -->
        <div class="print-order-band no-print">
            <div><!-- .container dropped: now nested in #odTop's container -->
                <button type="button"
                        class="btn btn-outline-dark btn-print-order"
                        @click="openPrintModal">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                         viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                         class="btn-print-icon">
                        <polyline points="6 9 6 2 18 2 18 9"></polyline>
                        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                        <rect x="6" y="14" width="12" height="8"></rect>
                    </svg>
                    {{ printButtonLabel }}
                </button>
            </div>
        </div>

        <!-- ===== GROUPED QUOTES (by Quote Reference → Machine + Assembly) =====
             Same visual treatment as OrderDetails. Each Quote Reference
             is one "Quote #N" group; within each group, each unique
             Machine + Assembly combo is its own card with collapsible
             parts. Parts duplicated across multiple backing PurchaseOrder
             records (same Quote Reference + machine + assembly) merge
             into a single row with summed quantities. View is read-only. -->
        <div id="OrderTable" class="jcs-print-doc">
            <!-- ===== PER-QUOTE SECTIONS =====
                 Each Quote # group is wrapped in a `print-quote-section`
                 so the print stylesheet can:
                   1. Insert a `page-break-before: always` between
                      consecutive sections — every quote starts on its
                      own page in the printed output.
                   2. Hide a section entirely when the user picked a
                      subset to print (printingSelectedOnly flag set,
                      this group's number not in selectedQuotesForPrint).
                      The `print-quote-hidden` class drives that via
                      an @media print { display: none } rule.
                 Outside print (default screen view) the wrapper is
                 a transparent passthrough — no visual change. -->
            <section class="print-quote-section"
                     v-for="(group, groupIndex) in groupedOrders"
                     :key="'qr-' + group.number"
                     :class="{ 'print-quote-hidden': isQuoteHiddenForPrint(group.number) }">
                <!-- ===== PER-QUOTE PRINT-ONLY LETTERHEAD =====
                     Identical markup and behavior to the legacy
                     page-level header, just relocated so each quote
                     carries its own copy. Hidden on screen via the
                     scoped .print-only-header { display: none } rule;
                     revealed in print by the @media print { display:
                     flex } rule at the bottom of the file.

                     Two logo strategies stacked: <img> first (same
                     SVG used by Head.vue), text "STEELE" fallback
                     if the SVG fails to load. printLogoFailed is
                     shared across every header instance — one
                     failed load flips the flag for all of them,
                     which is the correct behavior since they all
                     reference the same asset. -->
                <div class="print-only-header">
                    <div class="print-only-header-logo">
                        <img src="@/assets/img/steele-logo.svg"
                             alt="JC Steele"
                             class="print-only-header-logo-img"
                             @error="onPrintLogoError"
                             v-if="!printLogoFailed" />
                        <div class="print-only-header-logo-text"
                             v-if="printLogoFailed">
                            STEELE
                        </div>
                    </div>
                    <!-- Center: customer name + ID, between the logo and the
                         doc-id / Parts Department block. -->
                    <div class="print-only-header-customer">
                        <div class="print-only-header-customer-name">{{ company }}</div>
                        <div class="print-only-header-customer-id" v-if="companyCode">Customer ID: {{ companyCode }}</div>
                    </div>
                    <div class="print-only-header-address">
                        <!-- Doc identifier (Order #, Quote #, or
                             Acknowledgement #) — print-only,
                             renders at the top of every printed
                             page so the recipient can tie the
                             output back to a specific record. -->
                        <div class="print-only-header-doc-id">Quote #: {{ id }}</div>
                        <div class="print-only-header-address-name">Parts Department</div>
                        <div>145 National Dr.</div>
                        <div>Statesville, NC 28625</div>
                        <div>P. (800) 278-3353</div>
                    </div>
                </div>

                <div><!-- .container dropped: now nested in #odTop's container -->
                    <div class="po-group">
                        <div class="order-header">
                            <span class="order-header-label">Quote Reference #{{ group.number }}</span>
                            <span class="order-header-meta">
                                {{ group.machineGroups.length }}
                                {{ group.machineGroups.length === 1 ? 'item' : 'items' }}
                            </span>
                        </div>

                        <div class="assembly-card"
                             v-for="(mg, mgIndex) in group.machineGroups"
                             :key="'asm-' + group.number + '-' + mg.key">
                            <div class="assembly-card-header">
                                <div class="assembly-card-heading">
                                    <div class="assemblyName">{{ mg.machineName }}</div>
                                    <div class="assemblyDescription">{{ mg.assemblyName }}</div>
                                </div>
                                <div class="assembly-card-po-subtotal" v-if="mgIndex === 0">
                                    <div class="assembly-card-po-label">
                                        Subtotal for Quote Reference:
                                        <span class="subtotal-po">{{ group.number || '—' }}</span>
                                    </div>
                                    <div class="assembly-card-po-amount" v-if="group.cost > 0">
                                        {{ group.cost | currency }}
                                    </div>
                                    <div class="assembly-card-po-amount" v-else>$0.00</div>
                                </div>
                            </div>

                            <div class="assembly-summary"
                                 :class="{ expanded: isGroupExpanded(group.number, mg.key) }"
                                 @click="toggleGroup(group.number, mg.key)">
                                <div class="assembly-toggle"
                                     :class="{ expanded: isGroupExpanded(group.number, mg.key) }"
                                     :aria-label="isGroupExpanded(group.number, mg.key) ? 'Collapse' : 'Expand'">
                                    <span class="assembly-toggle-icon">{{ isGroupExpanded(group.number, mg.key) ? '−' : '+' }}</span>
                                </div>
                                <div class="assembly-summary-content">
                                    <span class="assembly-summary-count">
                                        {{ mg.mergedParts.length }} {{ mg.mergedParts.length === 1 ? 'part' : 'parts' }}
                                    </span>
                                    <span class="code-chip"
                                          v-for="p in mg.mergedParts"
                                          :key="'chip-' + group.number + '-' + mg.key + '-' + (p.part && p.part.code)">
                                        {{ p.part && p.part.code }}<span class="code-qty">×{{ p.quantity }}</span>
                                    </span>
                                    <span class="assembly-summary-empty" v-if="mg.mergedParts.length === 0">
                                        No parts on this assembly.
                                    </span>
                                </div>
                                <div class="assembly-summary-total"
                                     v-if="group.machineGroups.length > 1">
                                    <!-- This per-assembly total is suppressed
                                         when the PO has only one assembly,
                                         because in that case it equals the
                                         PO subtotal already shown above
                                         (.assembly-card-po-subtotal) and the
                                         duplicate read as visual noise on
                                         single-assembly receipts. With more
                                         than one assembly, each assembly's
                                         own subtotal IS distinct from the
                                         PO grand total, so the breakdown
                                         stays useful. -->
                                    <span v-if="mg.cost > 0">{{ mg.cost | currency }}</span>
                                    <span v-else>$0.00</span>
                                </div>
                            </div>

                            <div class="assembly-table-wrap" v-show="isGroupExpanded(group.number, mg.key)">
                                <b-table :fields="partsFields" :items="mg.mergedParts" responsive="sm">
                                    <template v-slot:cell(item)="data">
                                        <div class="nowrap">{{ data.item.part && data.item.part.item }}</div>
                                    </template>
                                    <template v-slot:cell(code)="data">
                                        <div class="nowrap">{{ data.item.part && data.item.part.code }}</div>
                                    </template>
                                    <template v-slot:cell(description)="data">
                                        <div class="text-left">{{ data.item.part && data.item.part.description }}</div>
                                    </template>
                                    <template v-slot:cell(qty)="data">
                                        {{ data.item.part && data.item.part.quantityPerMachine }}
                                    </template>
                                    <template v-slot:cell(price)="data">
                                        {{ (data.item.part && data.item.part.price) | currency }}
                                    </template>
                                    <template v-slot:cell(quoteqty)="data">
                                        {{ data.item.quantity }}
                                    </template>
                                    <template v-slot:cell(cost)="data">
                                        {{ data.item.cost | currency }}
                                    </template>
                                </b-table>
                                <!-- Print-only totals line, below (not inside) the table. -->
                                <div class="parts-total-line">
                                    <span class="parts-total-strong">Total:</span>
                                    <span class="parts-total-amount">{{ mg.mergedParts.reduce((s, p) => s + (Number(p.quantity) || 0), 0) }}</span> Parts at
                                    <span class="parts-total-amount">{{ mg.mergedParts.reduce((s, p) => s + (Number(p.cost) || 0), 0) | currency }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- Per-reference total: each Quote Reference prints on its
                         own page and ends with its own bottom-line total. -->
                    <div class="cart-grand-total">
                        <div class="cart-grand-total-label">Quote Total:</div>
                        <div class="cart-grand-total-value"><span class="grand-total-parts-prefix">{{ groupPartsCount(group) }} Parts at </span>{{ group.cost | currency }}</div>
                    </div>
                </div>
            </section>

            <!-- The Quote Total now prints per Quote Reference, inside each
                 section above, so each reference's page ends with its own
                 bottom-line total. -->
        </div><!-- /#OrderTable -->
            </div><!-- /.container (#odTop) — wraps the whole left content now -->
        </div><!-- /#odTop -->

        <!-- ===== PRINT QUOTE(S) MODAL =====
             Opens when the user clicks the Print Quote(s) button
             in the .print-order-band above. Always shown on click —
             even for the single-quote case — so the user has a
             consistent confirmation step that mirrors the
             Finalize / Print Selected Quotes flow on
             /cart/rfqsaveditems and the matching modal on the
             SavedRFQ success page.

             Two flavors driven off groupedOrders.length:
               1 quote   — banner reads "Ready to Print Your Quote?",
                           no checkboxes (one row, always selected),
                           primary button reads "Print Quote".
               N quotes  — banner reads "Ready to Print Quotes?",
                           a checkbox per quote so the user can
                           deselect ones they don't want to print,
                           primary button reads "Print All Quotes"
                           when everything's selected, "Print N
                           Selected Quotes" when a subset is.

             no-print on the modal-class so an accidentally-open
             dialog doesn't bleed into the print output. -->
        <b-modal id="print-quote-modal"
                 v-model="showPrintModal"
                 :title="printModalTitle"
                 size="md"
                 centered
                 modal-class="print-quote-modal no-print"
                 :ok-title="printModalConfirmLabel"
                 ok-variant="primary"
                 cancel-title="Cancel"
                 cancel-variant="outline-secondary"
                 :ok-disabled="!hasSelectedQuotesForPrint"
                 @ok.prevent="confirmPrint">

            <!-- Brand-red banner — same visual treatment as the
                 Modify Quote / Finalize modals on /cart/rfqsaveditems
                 so the two flows feel consistent. Title varies by
                 single vs multi-quote case (see printModalBannerTitle
                 computed). -->
            <div class="modify-restored-banner print-quote-banner">
                <div class="modify-restored-title">
                    {{ printModalBannerTitle }}
                </div>
                <div class="modify-restored-body">
                    <span v-if="groupedOrders.length === 1">
                        Confirm below — then click <strong>Print Quote</strong>
                        to open the printable quote receipt.
                    </span>
                    <span v-else>
                        Confirm the quotes below — then click
                        <strong>{{ printModalConfirmLabel }}</strong> to open
                        the printable quote receipt. Each selected quote
                        prints on its own page with the JC Steele
                        letterhead at the top.
                    </span>
                </div>
            </div>

            <p class="convert-modal-intro" v-if="groupedOrders.length > 1">
                Select which quotes you want to include on the printed
                output.
            </p>

            <!-- Quote rows. For a single quote we still render the
                 row (just without a selection checkbox) so the user
                 sees what they're about to print. For multiple quotes,
                 each row gets a b-form-checkbox bound by the quote
                 number — same container/styling vocabulary
                 (.convert-quote-list, .convert-quote-row,
                 .convert-quote-row-grouped, etc.) used by the
                 Finalize / Save for Later picker on
                 /cart/rfqsaveditems so the pages feel like siblings.

                 Per-row content mirrors the on-page layout:
                 Quote #{number}, assembly count, total cost,
                 and a small list of each constituent assembly. -->
            <div class="convert-quote-list">
                <div v-for="(group, groupIndex) in groupedOrders"
                     :key="'print-pick-' + group.number"
                     class="convert-quote-row convert-quote-row-grouped">
                    <!-- Multi-quote: bound checkbox per row. -->
                    <b-form-checkbox v-if="groupedOrders.length > 1"
                                     :checked="isQuoteSelectedForPrint(group.number)"
                                     @change="setQuoteSelectionForPrint(group.number, $event)">
                        <div class="convert-quote-row-main">
                            <strong class="convert-quote-row-label">
                                Quote Reference #{{ group.number }}
                            </strong>
                        </div>
                        <div class="convert-quote-row-sub">
                            {{ group.machineGroups.length }}
                            {{ group.machineGroups.length === 1 ? 'item' : 'items' }}
                            <span class="convert-quote-row-amount"
                                  v-if="group.cost > 0">
                                · {{ group.cost | currency }}
                            </span>
                        </div>
                        <div class="convert-quote-row-assemblies"
                             v-if="group.machineGroups.length > 0">
                            <div v-for="mg in group.machineGroups"
                                 :key="'print-pick-asm-' + group.number + '-' + mg.key"
                                 class="convert-quote-row-assembly">
                                <span v-if="mg.machineName">{{ mg.machineName }}</span>
                                <template v-if="mg.assemblyName"> · {{ mg.assemblyName }}</template>
                                <template v-if="mg.mergedParts && mg.mergedParts.length">
                                    · {{ mg.mergedParts.length }}
                                    {{ mg.mergedParts.length === 1 ? 'part' : 'parts' }}
                                </template>
                            </div>
                        </div>
                    </b-form-checkbox>

                    <!-- Single-quote: no checkbox, just the same row
                         layout as the multi-quote case minus the form
                         control. .print-quote-row-static is the wrapper
                         that replaces b-form-checkbox in that branch. -->
                    <div v-else class="print-quote-row-static">
                        <div class="convert-quote-row-main">
                            <strong class="convert-quote-row-label">
                                Quote Reference #{{ group.number }}
                            </strong>
                        </div>
                        <div class="convert-quote-row-sub">
                            {{ group.machineGroups.length }}
                            {{ group.machineGroups.length === 1 ? 'item' : 'items' }}
                            <span class="convert-quote-row-amount"
                                  v-if="group.cost > 0">
                                · {{ group.cost | currency }}
                            </span>
                        </div>
                        <div class="convert-quote-row-assemblies"
                             v-if="group.machineGroups.length > 0">
                            <div v-for="mg in group.machineGroups"
                                 :key="'print-static-asm-' + group.number + '-' + mg.key"
                                 class="convert-quote-row-assembly">
                                <span v-if="mg.machineName">{{ mg.machineName }}</span>
                                <template v-if="mg.assemblyName"> · {{ mg.assemblyName }}</template>
                                <template v-if="mg.mergedParts && mg.mergedParts.length">
                                    · {{ mg.mergedParts.length }}
                                    {{ mg.mergedParts.length === 1 ? 'part' : 'parts' }}
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Select All / Deselect All toggle — only when there's
                 more than one quote (single-quote case has no
                 checkbox to flip anyway). Mirrors the Finalize modal
                 on /cart/rfqsaveditems for visual consistency. -->
            <div class="convert-quote-toggle-all"
                 v-if="groupedOrders.length > 1">
                <a href="#"
                   @click.prevent="toggleSelectAllQuotesForPrint">
                    {{ selectedQuotesForPrint.length === groupedOrders.length
                        ? 'Deselect All'
                        : 'Select All' }}
                </a>
            </div>
        </b-modal>
    </section>
</template>

<script>
import FeApiService from "@/assets/js/FeApiService";
const apiService = new FeApiService();

import 'expose-loader?$!expose-loader?jQuery!jquery';
import 'semantic-ui-dropdown/dropdown.js';

import Vue from "vue";
import Vue2Filters from "vue2-filters";

Vue.use(Vue2Filters);

export default {
    name: 'RFQDetails',
    computed: {
        // Two-level grouping mirroring the Cart and OrderDetails pages:
        //   1. By Quote Reference (top level — one "Quote #N" block each)
        //   2. Within each reference, by Machine + Assembly combination
        // Within each subgroup, parts are deduped by part code (qty +
        // cost summed) so duplicate backing PurchaseOrder records
        // collapse into a clean per-part view. Read-only — no _sources
        // tracking since nothing on this page can be updated/removed.
        groupedOrders() {
            return this.buildGroupedOrders(this.orderDetails);
        },

        // Total quantity of all parts across every quote ref/assembly —
        // the "N Parts" shown beside the grand total on the printout.
        totalPartsCount() {
            var n = 0;
            (this.groupedOrders || []).forEach(function (g) {
                (g.machineGroups || []).forEach(function (mg) {
                    (mg.mergedParts || []).forEach(function (p) {
                        n += Number(p.quantity) || 0;
                    });
                });
            });
            return n;
        },

        // ===== PRINT BUTTON / MODAL TEXT (singular vs plural) =====
        // Drives the visible label on the .btn-print-order button
        // and the modal title. Singular / plural based purely on
        // groupedOrders.length so a one-quote saved RFQ reads
        // naturally instead of "Print Quotes" with one item.
        printButtonLabel() {
            return this.groupedOrders.length > 1 ? 'Print Quotes' : 'Print Quote';
        },
        printModalTitle() {
            return this.groupedOrders.length > 1 ? 'Print Quotes' : 'Print Quote';
        },

        // ===== BANNER TITLE =====
        // The big red callout at the top of the modal. Singular
        // form for the one-quote case — "Ready to Print Your
        // Quote?". Plural form covers the more common
        // multi-Quote-Reference case.
        printModalBannerTitle() {
            return this.groupedOrders.length > 1
                ? 'Ready to Print Quotes?'
                : 'Ready to Print Your Quote?';
        },

        // ===== CONFIRM BUTTON LABEL =====
        // Three flavors:
        //   - Single quote                                 → "Print Quote"
        //   - Multi quote, all selected                    → "Print All Quotes"
        //   - Multi quote, partial selection               → "Print N Selected Quotes"
        // Matches the dynamic-label pattern the Finalize /
        // Print modal uses on /cart/rfqsaveditems and the
        // SavedRFQ success page so the user gets useful
        // feedback about what the action is going to do.
        printModalConfirmLabel() {
            var total = this.groupedOrders.length;
            if (total <= 1) return 'Print Quote';
            var picked = this.selectedQuotesForPrint.length;
            if (picked === 0) return 'Print';
            if (picked === total) return 'Print All Quotes';
            return 'Print ' + picked + ' Selected ' +
                (picked === 1 ? 'Quote' : 'Quotes');
        },

        // ===== CONFIRM BUTTON ENABLE STATE =====
        // Disable Print when nothing is checked. Single-quote
        // case is always enabled because there's nothing to
        // uncheck (the row renders without a checkbox at all).
        hasSelectedQuotesForPrint() {
            if (this.groupedOrders.length <= 1) return true;
            return this.selectedQuotesForPrint.length > 0;
        },
    },
    mounted() {
        this.rfqOrder();
    },
    data() {
        return {
            id: "",
            specialInstructions: "",
            orderDate: "",
            orderDetails: [],
            total: "",
            // Print-header logo error fallback. Flipped true
            // by onPrintLogoError when the <img> fails to
            // load, swapping the markup to a text "STEELE"
            // fallback so the printed receipt always carries
            // a brand mark.
            printLogoFailed: false,
            company: "",
            companyCode: "",
            billingCompanyName: "",
            billingStreet: "",
            billingStreet2: "",
            billingCity: "",
            billingState: "",
            billingPostalCode: "",
            shippingCompanyName: "",
            shippingStreet: "",
            shippingStreet2: "",
            shippingCity: "",
            shippingState: "",
            shippingPostalCode: "",
            // Tracks which assembly cards are expanded — keyed by
            // Quote Reference + machine/assembly key. All start
            // collapsed for a clean overview-first view.
            expandedGroups: {},
            partsFields: [
                'item',
                'code',
                'description',
                { key: 'qty', label: 'QTY PER MACHINE' },
                'price',
                { key: 'quoteqty', label: 'QUOTED' },
                'cost'
            ],
            clear: '',

            // ===== PRINT MODAL STATE =====
            // showPrintModal — drives the b-modal v-model. Flipped
            // true by openPrintModal (button click), back to false
            // by Cancel / dismiss / successful print.
            //
            // selectedQuotesForPrint — array of quote.number values
            // representing which quotes will appear in the print
            // output. Populated to "all of them" when the modal
            // opens; the user can deselect rows in the multi-quote
            // case. confirmPrint short-circuits if this is empty
            // (the modal's primary button is disabled in that
            // state, but we double-check).
            //
            // printingSelectedOnly — set true for the duration of
            // window.print(). The per-quote section wrapper hides
            // itself in print when this flag is true AND the
            // section's quote.number isn't in
            // selectedQuotesForPrint, via the print-quote-hidden
            // class plumbed through isQuoteHiddenForPrint(). Reset
            // to false in confirmPrint's finally so a stale flag
            // can't suppress a future full-page print.
            showPrintModal: false,
            selectedQuotesForPrint: [],
            printingSelectedOnly: false,
        }
    },
    methods: {
        // Total quantity of all parts within ONE Quote-reference group —
        // drives the "N Parts" on that reference's bottom-line total.
        groupPartsCount(group) {
            var n = 0;
            (group.machineGroups || []).forEach(function (mg) {
                (mg.mergedParts || []).forEach(function (p) {
                    n += Number(p.quantity) || 0;
                });
            });
            return n;
        },
        // Logo <img> in the print header failed to load —
        // flip the flag so the v-if swaps to the text
        // fallback ("STEELE") visible in the next paint,
        // before the user triggers print.
        onPrintLogoError() {
            this.printLogoFailed = true;
        },

        // ===== PRINT MODAL: OPEN =====
        // Click handler for the .btn-print-order button. Opens
        // the confirmation modal (always — even for a single
        // quote, so the experience is consistent across counts).
        // Pre-selects every quote so "Print All" is the
        // zero-click default; the user only has to interact
        // when they want a subset.
        //
        // Edge case: groupedOrders is empty (no orders loaded
        // yet, or rfqOrder() failed). Surface a toast and skip
        // opening the modal — matches the same not-ready-yet
        // pattern used for placeOrder() on related pages.
        openPrintModal() {
            if (!this.groupedOrders || this.groupedOrders.length === 0) {
                this.$bvToast.toast(
                    "Quote data isn't ready yet — please try again in a moment.",
                    {
                        title: 'Cannot print',
                        autoHideDelay: 4000,
                        variant: 'warning',
                        solid: true,
                    }
                );
                return;
            }
            // Reset selection to "everything checked" each time
            // the modal opens so a previous partial selection
            // doesn't linger. Quote refs are the source-of-truth
            // identifier for matching against the per-quote
            // print sections.
            this.selectedQuotesForPrint = this.groupedOrders.map(function (g) {
                return g.number;
            });
            this.showPrintModal = true;
        },

        // ===== PRINT MODAL: CONFIRM =====
        // Triggered by the modal's primary button. Sets the
        // printingSelectedOnly flag (which the per-quote section
        // wrapper consumes via isQuoteHiddenForPrint), waits
        // for Vue to flush the class change to the DOM, then
        // fires window.print(). The browser's print dialog is
        // modal — by the time it returns we're safe to roll
        // the flag back so a subsequent unrelated print (e.g.
        // browser hotkey) shows the full page.
        //
        // Wrapped in try/finally so an exception during print
        // (rare, but possible — some embedded contexts
        // disallow it) doesn't strand the page in
        // printingSelectedOnly=true state, which would silently
        // hide quote sections on future prints.
        confirmPrint() {
            var self = this;
            // Defensive — ok-disabled covers the multi-quote
            // empty case, but this also gates accidental
            // programmatic calls.
            if (!this.hasSelectedQuotesForPrint) return;

            this.printingSelectedOnly = true;
            this.showPrintModal = false;

            // Wait for the section wrappers to pick up the
            // print-quote-hidden class via reactivity. Without
            // nextTick, window.print() runs against the
            // pre-update DOM and unselected quotes still
            // appear.
            this.$nextTick(function () {
                try {
                    window.print();
                } finally {
                    // Reset on the NEXT tick so any frame the
                    // print engine captures during a slow
                    // dialog open still has the hidden state
                    // applied. window.print() is synchronous
                    // in mainstream browsers, but the
                    // belt-and-braces approach here costs
                    // nothing.
                    self.$nextTick(function () {
                        self.printingSelectedOnly = false;
                    });
                }
            });
        },

        // ===== PRINT SELECTION HELPERS =====
        // isQuoteSelectedForPrint — checkbox v-bind helper.
        // isQuoteHiddenForPrint   — wrapper class helper.
        //                           Returns true when we're in
        //                           selected-only print mode
        //                           AND this quote isn't on the
        //                           picked list. The wrapper
        //                           toggles print-quote-hidden
        //                           based on the result; the
        //                           @media print rule does the
        //                           actual hiding.
        // setQuoteSelectionForPrint — checkbox @change handler.
        // toggleSelectAllQuotesForPrint — Select / Deselect
        //                                 All link at the
        //                                 bottom of the modal.
        isQuoteSelectedForPrint(quoteRef) {
            return this.selectedQuotesForPrint.indexOf(quoteRef) !== -1;
        },
        isQuoteHiddenForPrint(quoteRef) {
            if (!this.printingSelectedOnly) return false;
            return !this.isQuoteSelectedForPrint(quoteRef);
        },
        setQuoteSelectionForPrint(quoteRef, checked) {
            var idx = this.selectedQuotesForPrint.indexOf(quoteRef);
            if (checked && idx === -1) {
                // Push a copy so v-model bindings further up
                // see a new array reference and re-render
                // reliably (Vue 2 can miss in-place mutations
                // of arrays held on data props in some edge
                // cases).
                this.selectedQuotesForPrint = this.selectedQuotesForPrint.concat([quoteRef]);
            } else if (!checked && idx !== -1) {
                var next = this.selectedQuotesForPrint.slice();
                next.splice(idx, 1);
                this.selectedQuotesForPrint = next;
            }
        },
        toggleSelectAllQuotesForPrint() {
            // If everything is currently checked, clear;
            // otherwise select all. Mirrors the Finalize modal's
            // same toggle.
            if (this.selectedQuotesForPrint.length === this.groupedOrders.length) {
                this.selectedQuotesForPrint = [];
            } else {
                this.selectedQuotesForPrint = this.groupedOrders.map(function (g) {
                    return g.number;
                });
            }
        },

        // Build the two-level grouped structure consumed by the template.
        // Note: this page's API returns `order.machine.description` (NOT
        // `order.assembly.machine.description` like the cart payload) and
        // `order.assembly.name`, plus parts as line items with the nested
        // `lineItem.part.<field>` shape — same as Cart and SavedRFQ,
        // different from OrderDetails which has flat fields.
        buildGroupedOrders(orders) {
            var poGroups = {};

            (orders || []).forEach(function (order) {
                var quoteRef = order.number || '(no reference)';

                // --- Quote Reference level ---
                if (!poGroups[quoteRef]) {
                    poGroups[quoteRef] = {
                        number: order.number || '',
                        cost: 0,
                        _machineMap: {}
                    };
                }
                var poGroup = poGroups[quoteRef];

                // --- Machine + Assembly level ---
                var machineName = (order.machine && order.machine.description) || '';
                var assemblyName = (order.assembly && order.assembly.name) || '';
                var mKey = machineName + '||' + assemblyName;

                if (!poGroup._machineMap[mKey]) {
                    poGroup._machineMap[mKey] = {
                        key: mKey,
                        machineName: machineName,
                        assemblyName: assemblyName,
                        cost: 0,
                        _partsMap: {}
                    };
                }
                var mGroup = poGroup._machineMap[mKey];
                mGroup.cost += Number(order.cost) || 0;
                poGroup.cost += Number(order.cost) || 0;

                // --- Part dedup level ---
                // Keys on part code so duplicate part rows under the
                // same machine/assembly collapse into one row with
                // summed quantity and cost.
                (order.parts || []).forEach(function (lineItem) {
                    if (!lineItem || !lineItem.part) return;
                    var code =
                        lineItem.part.code ||
                        lineItem.part.item ||
                        ('__id_' + lineItem.id);
                    if (!mGroup._partsMap[code]) {
                        mGroup._partsMap[code] = {
                            id: lineItem.id,
                            part: lineItem.part,
                            quantity: Number(lineItem.quantity) || 0,
                            cost: Number(lineItem.cost) || 0
                        };
                    } else {
                        mGroup._partsMap[code].quantity += Number(lineItem.quantity) || 0;
                        mGroup._partsMap[code].cost += Number(lineItem.cost) || 0;
                    }
                });
            });

            return Object.keys(poGroups).map(function (poKey) {
                var po = poGroups[poKey];
                return {
                    number: po.number,
                    cost: po.cost,
                    machineGroups: Object.keys(po._machineMap).map(function (mKey) {
                        var mg = po._machineMap[mKey];
                        return {
                            key: mg.key,
                            machineName: mg.machineName,
                            assemblyName: mg.assemblyName,
                            cost: mg.cost,
                            mergedParts: Object.keys(mg._partsMap).map(function (k) {
                                return mg._partsMap[k];
                            })
                        };
                    })
                };
            });
        },

        // ===== ASSEMBLY EXPAND/COLLAPSE =====
        groupExpandKey(quoteRef, mgKey) {
            return quoteRef + '__' + mgKey;
        },
        isGroupExpanded(quoteRef, mgKey) {
            return !!this.expandedGroups[this.groupExpandKey(quoteRef, mgKey)];
        },
        toggleGroup(quoteRef, mgKey) {
            var k = this.groupExpandKey(quoteRef, mgKey);
            this.$set(this.expandedGroups, k, !this.expandedGroups[k]);
        },

        // Initial fetch — populates the page from the saved-quote API.
        rfqOrder() {
            apiService
                .rfqOrder({
                    id: this.$route.params.id
                })
                .then(request => {
                    this.rfqOrderSuccessful(request);
                })
                .catch(err => this.rfqOrderFailed(err));
        },
        rfqOrderSuccessful(request) {
            (this.id = request.id),
                (this.specialInstructions = request.specialInstructions),
                (this.orderDate = request.dateCreated),
                (this.orderDetails = request.purchaseOrders),
                (this.total = request.cost),
                (this.company = request.company.name),
                (this.companyCode = request.company.code),
                (this.billingCompanyName = request.company.plant.name),
                (this.billingStreet = request.company.plant.street),
                (this.billingStreet2 = request.company.plant.street2),
                (this.billingCity = request.company.plant.city),
                (this.billingState = request.company.plant.state),
                (this.billingPostalCode = request.company.plant.postalCode),
                (this.shippingCompanyName = request.company.shipTo.name),
                (this.shippingStreet = request.company.shipTo.street),
                (this.shippingStreet2 = request.company.shipTo.street2),
                (this.shippingCity = request.company.shipTo.city),
                (this.shippingState = request.company.shipTo.state),
                (this.shippingPostalCode = request.company.shipTo.postalCode);
        },
        rfqOrderFailed(err) {
            this.$bvToast.toast("TraceID: " + err.error.response.data.traceId, {
                title: err.error.response.data.title,
                autoHideDelay: 10000,
                appendToast: false,
                variant: "danger",
                solid: true
            });
        },
        // "Convert to Order" — promotes this saved quote into an actual
        // order cart. Calls the rfqOrder API again (the existing flow's
        // convention — the backend evidently returns a cart-shaped
        // response for the order side), then sets localStorage.cartId
        // and pushes to the Cart route. Renamed from `order(id)` for
        // clarity on what the action does. If the API or backend
        // convention changes (e.g. a dedicated convert-to-order
        // endpoint), the apiService call below is the spot to swap.
        convertToOrder(id) {
            apiService
                .rfqOrder({
                    id: id
                })
                .then(request => {
                    this.convertToOrderSuccessful(request);
                })
                .catch(err => this.convertToOrderFailed(err));
        },
        convertToOrderSuccessful(request) {
            localStorage.cartId = request.id;
            this.$router.push({
                name: "Cart",
                params: {
                    id: request.id
                }
            });
        },
        convertToOrderFailed(err) {
            this.$bvToast.toast("TraceID: " + err.error.response.data.traceId, {
                title: err.error.response.data.title,
                autoHideDelay: 10000,
                appendToast: false,
                variant: "danger",
                solid: true
            });
        },
        showDeleteModal(id) {
            this.$bvModal
                .msgBoxConfirm(
                    "PLEASE CONFIRM YOU WANT TO DELETE THIS QUOTE",
                    {
                        title: "DELETE THIS QUOTE?",
                        id: "deleteModal",
                        size: "md",
                        buttonSize: "sm",
                        okVariant: "dark",
                        okTitle: "Yes, please delete",
                        cancelVariant: "outline",
                        cancelTitle: "No, please don’t",
                        hideHeaderClose: true,
                        centered: true
                    }
                )
                .then(clear => {
                    if (clear && id) {
                        apiService
                            .deleteQuote({
                                id: id
                            })
                            .then(request => {
                                // If the deleted quote was the active
                                // RFQ cart, clear those localStorage
                                // entries so the user doesn't get
                                // dropped back into a stale cart.
                                if (localStorage.cartIdRFQ == id) {
                                    delete localStorage.cartIdRFQ;
                                    delete localStorage.poNumberRFQ;
                                }
                                this.deleteSuccessful(request);
                            })
                            .catch(err => this.deleteFailed(err));
                    }
                })
                .catch(err => this.deleteFailed(err));
        },
        deleteSuccessful() {
            this.$router.push({
                name: "RFQ History"
            });
        },
        deleteFailed(err) {
            this.$bvToast.toast("TraceID: " + err.error.response.data.traceId, {
                title: err.error.response.data.title,
                autoHideDelay: 10000,
                appendToast: false,
                variant: "danger",
                solid: true
            });
        }
    }
};
</script>

<style scoped>
/* ===== PRINT-ONLY HEADER (HIDDEN ON SCREEN) =====
   The letterhead-style banner at the top of the template
   appears ONLY in print output. Hidden by default here so
   it doesn't show in the live UI; revealed by the @media
   print rule in the non-scoped style block at the bottom
   of this file. */
.print-only-header {
    display: none;
}

/* ===== PAGE CONTAINER =====
   position: relative gives the section its own stacking context so
   nothing inside accidentally floats above the fixed-position side
   rails (body::before / body::after at z-index 999). */
#orderDetails {
    position: relative;
}

/* ===== PAGE HEADER (Quote # + Date) ===== */
.order-page-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 20px;
    flex-wrap: wrap;
    padding-bottom: 16px;
    margin-bottom: 24px;
    border-bottom: 1px dashed #d8d8d8;
}

.order-page-header-main h1 {
    margin: 0 0 4px;
    font-size: 26px;
    font-weight: 700;
    color: #222;
    letter-spacing: 0.2px;
}

.order-page-header-meta {
    margin: 0;
    font-size: 13px;
    color: #777;
}

/* ===== HEADER ACTION BUTTONS =====
   Sit on the right side of the page header row, opposite the Quote #
   title. Convert to Order is the red-filled primary commit; Delete
   Quote is dark to indicate the destructive secondary action. The
   pricing-changes disclaimer sits beneath the buttons in small italic
   gray. On narrow viewports (<576px) the buttons stretch to fill the
   row and the disclaimer left-aligns. */
.order-page-header-actions {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 6px;
}

.header-action-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: flex-end;
}

.header-action-buttons .btn-sm {
    font-size: 12px;
    padding: 7px 18px;
    letter-spacing: 0.3px;
    min-width: 130px;
}

.header-action-disclaimer {
    margin: 0;
    font-size: 11px;
    color: #888;
    font-style: italic;
    text-align: right;
}

@media (max-width: 575.98px) {
    .order-page-header-actions {
        width: 100%;
        align-items: stretch;
        margin-top: 12px;
    }
    .header-action-buttons .btn-sm {
        flex: 1 1 auto;
    }
    .header-action-disclaimer {
        text-align: left;
    }
}

/* ===== SPECIAL INSTRUCTIONS BLOCK ===== */
.special-instructions-block {
    background: #fafafa;
    border: 1px solid #e8e8e8;
    border-left: 3px solid #888;
    border-radius: 6px;
    padding: 14px 18px;
    margin-bottom: 28px;
}

.special-instructions-label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: #555;
    margin-bottom: 6px;
}

.special-instructions-text {
    font-size: 13px;
    color: #444;
    line-height: 1.5;
    white-space: pre-wrap;
}

/* ===== PO/QUOTE GROUP (the grouping container) ===== */
.po-group {
    background: #fafafa;
    border: 1px solid #e8e8e8;
    border-left: 3px solid #c0392b;
    border-radius: 8px;
    margin: 0 0 28px;
    padding: 24px 22px 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.order-header {
    display: flex;
    align-items: baseline;
    gap: 12px;
    margin-bottom: 12px;
    padding-bottom: 10px;
    border-bottom: 1px dashed #d8d8d8;
}

.order-header-label {
    font-size: 20px;
    font-weight: 700;
    color: #c0392b;
    letter-spacing: 0.3px;
}

.order-header-meta {
    font-size: 12px;
    color: #777;
    font-style: italic;
}

/* ===== ASSEMBLY CARD ===== */
.assembly-card {
    background: #fff;
    border: 1px solid #ececec;
    border-radius: 6px;
    padding: 18px 20px 14px;
    margin-bottom: 16px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}

.assembly-card:last-of-type {
    margin-bottom: 18px;
}

.assembly-card-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 14px;
}

.assembly-card-heading {
    flex: 1 1 auto;
    min-width: 200px;
}

.assembly-card-po-subtotal {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 14px;
    background: #fafafa;
    border: 1px solid #e8e8e8;
    border-radius: 6px;
    padding: 8px 14px;
}

.assembly-card-po-label {
    font-size: 12px;
    color: #555;
    white-space: nowrap;
}

.assembly-card-po-amount {
    font-size: 16px;
    font-weight: 700;
    color: #c0392b;
    white-space: nowrap;
    letter-spacing: 0.3px;
}

@media (max-width: 767.98px) {
    .assembly-card-header {
        flex-direction: column;
    }
    .assembly-card-po-subtotal {
        align-self: stretch;
        justify-content: space-between;
    }
}

.assemblyName {
    font-size: 20px;
    font-weight: 700;
    color: #333;
    margin-bottom: 2px;
}

.assemblyDescription {
    font-size: 13px;
    color: #777;
}

.subtotal-po {
    color: #c0392b;
    font-weight: 700;
}

/* ===== COLLAPSIBLE ASSEMBLY SUMMARY ROW ===== */
.assembly-summary {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 10px 12px 10px 10px;
    background: #fafafa;
    border: 1px solid #eee;
    border-radius: 6px;
    cursor: pointer;
    margin-top: 4px;
    transition: background 0.15s ease, border-color 0.15s ease;
}

.assembly-summary:hover {
    background: #f3f3f3;
    border-color: #e0e0e0;
}

.assembly-summary.expanded {
    background: #fff;
    border-color: #ddd;
}

.assembly-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    min-width: 30px;
    border-radius: 50%;
    border: 1.5px solid #c0392b;
    background: #fff;
    color: #c0392b;
    font-size: 20px;
    font-weight: 700;
    line-height: 1;
    flex-shrink: 0;
    transition: background 0.15s ease, color 0.15s ease, transform 0.15s ease;
}

.assembly-summary:hover .assembly-toggle {
    background: #c0392b;
    color: #fff;
}

.assembly-toggle.expanded {
    background: #333;
    border-color: #333;
    color: #fff;
}

.assembly-summary:hover .assembly-toggle.expanded {
    background: #555;
    border-color: #555;
}

.assembly-toggle-icon {
    display: block;
    line-height: 1;
    margin-top: -2px;
}

.assembly-summary-content {
    flex: 1 1 auto;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    min-width: 0;
}

.assembly-summary-count {
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #888;
    margin-right: 4px;
}

.code-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 10px;
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 14px;
    font-size: 12px;
    font-weight: 600;
    color: #333;
    white-space: nowrap;
}

.code-chip .code-qty {
    color: #c0392b;
    font-weight: 700;
    font-size: 12px;
}

.assembly-summary-empty {
    font-size: 13px;
    color: #999;
    font-style: italic;
}

.assembly-summary-total {
    flex-shrink: 0;
    font-size: 16px;
    font-weight: 700;
    color: #c0392b;
    min-width: 100px;
    text-align: right;
}

.assembly-table-wrap {
    margin-top: 12px;
    padding-top: 8px;
    border-top: 1px solid #f0f0f0;
}
/* The per-table Quoted/Cost totals line is print-only. */
.parts-total-line {
    display: none;
}
/* The "N Parts at" prefix on the grand total is print-only; on screen
   the grand total stays just the dollar amount. */
.grand-total-parts-prefix {
    display: none;
}

/* Screen: the parts table spans the full content width (mirrors the print
   fix further down) so its columns spread out and align with the headers
   instead of squishing into the left ~60%. The Description column (3rd)
   absorbs the slack; the rest size to their content. Scoped to >= lg so it
   doesn't fight the stacked mobile layout. */
.assembly-table-wrap table {
    width: 100% !important;
    table-layout: auto !important;
}
/* Parts table body cells: explicit dark text so the values read clearly on
   the white (light-mode) panel. Dark/Mid modes override this in
   darkmode.css / mid-mode.css; the @media print block below overrides it to
   pure black. The header band is styled separately and is left untouched. */
.assembly-table-wrap td {
    color: #212529 !important;
}
@media (min-width: 992px) {
    .assembly-table-wrap th,
    .assembly-table-wrap td {
        white-space: nowrap;
    }
    .assembly-table-wrap th:nth-child(3),
    .assembly-table-wrap td:nth-child(3) {
        white-space: normal;
        width: 99%;
    }
}

/* ===== QUOTE GRAND TOTAL =====
   Direct port of Cart's cart-grand-total: heavy black top border,
   centered uppercase label, large red amount. */
.cart-grand-total {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 18px 20px;
    margin-top: 8px;
    margin-bottom: 24px;
    background: #fff;
    border: 1px solid #e5e5e5;
    border-top: 3px solid #222;
    border-radius: 6px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.cart-grand-total-label {
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: #333;
}

.cart-grand-total-value {
    font-size: 22px;
    font-weight: 700;
    color: #c0392b;
    letter-spacing: 0.3px;
}

@media (max-width: 575.98px) {
    .cart-grand-total {
        padding: 14px 16px;
    }
    .cart-grand-total-value {
        font-size: 18px;
    }
}

/* ===== PRINT QUOTE BAND =====
   Centered Print button in its own breathing-room band.
   Same styling as the Order pages so the two flows feel
   consistent. Brand red default, black on hover. */
.print-order-band {
    padding: 32px 0;
    text-align: center;
    background: transparent;
}

.btn-print-order {
    display: inline-flex;
    /* Keep the button content-width and centered in the band — not
       stretched across the page. */
    width: auto;
    max-width: 280px;
    margin-left: auto;
    margin-right: auto;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 10px 24px;
    border-radius: 6px;
    background: #c0392b;
    border: 1px solid #c0392b;
    color: #fff;
    transition: background 200ms ease, color 200ms ease,
                border-color 200ms ease, box-shadow 200ms ease;
}

.btn-print-order:hover,
.btn-print-order:focus {
    background: #000;
    color: #fff;
    border-color: #000;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.btn-print-icon {
    display: inline-block;
}

/* ===== PER-QUOTE PRINT SECTION =====
   Wrapper around each quote group that gives the @media
   print rules a hook to (a) page-break between consecutive
   quotes and (b) hide a section entirely when the user
   picked a subset to print. On screen it's a transparent
   passthrough — no padding, no border, no visual change. */
.print-quote-section {
    /* Intentionally empty on screen — display:block default
       with no styling. Print rules at the bottom of this
       file do all the work. */
}

/* ===== PRINT QUOTE MODAL =====
   Modal-scoped styles for #print-quote-modal, mirroring the
   visual treatment of the Finalize / Print and Modify Quote
   modals on /cart/rfqsaveditems and the matching modal on
   the SavedRFQ success page so the three pages feel like
   siblings. The .modify-restored-banner / .convert-* class
   names are reused verbatim so any future tweaks to the
   picker styling there propagate here without divergence —
   the styles below scope them to .print-quote-modal so they
   don't leak globally. */
.print-quote-modal .modify-restored-banner.print-quote-banner {
    background: linear-gradient(135deg, #fff5f3 0%, #fff 100%);
    border: 2px solid #c0392b;
    border-radius: 10px;
    padding: 18px 22px;
    margin: 0 0 18px;
    text-align: center;
    box-shadow: 0 4px 16px rgba(192, 57, 43, 0.12),
                0 1px 3px rgba(0, 0, 0, 0.04);
}

.print-quote-modal .modify-restored-title {
    font-size: 20px;
    font-weight: 800;
    color: #c0392b;
    letter-spacing: 0.3px;
    margin-bottom: 6px;
    line-height: 1.2;
}

.print-quote-modal .modify-restored-body {
    font-size: 13px;
    color: #444;
    line-height: 1.5;
}

.print-quote-modal .modify-restored-body strong {
    color: #222;
    font-weight: 700;
}

@media (max-width: 575.98px) {
    .print-quote-modal .modify-restored-title {
        font-size: 18px;
    }
    .print-quote-modal .modify-restored-banner.print-quote-banner {
        padding: 14px 16px;
    }
}

/* Modal body intro line under the banner. Same sizing as
   the convert / save-for-later modals. */
.print-quote-modal .convert-modal-intro {
    margin: 0 0 14px;
    font-size: 13px;
    color: #555;
}

/* Quote pick rows. Container holds N rows, each rendering
   either a b-form-checkbox (multi-quote) or a static
   summary block (single-quote). Brand-red left border on
   the row treatment ties it to the on-page Quote # group
   styling. */
.print-quote-modal .convert-quote-list {
    margin: 0 0 12px;
    padding: 0;
}

.print-quote-modal .convert-quote-row {
    padding: 10px 12px;
    background: #fafafa;
    border: 1px solid #ececec;
    border-radius: 6px;
    margin-bottom: 6px;
}

.print-quote-modal .convert-quote-row:last-child {
    margin-bottom: 0;
}

.print-quote-modal .convert-quote-row-grouped {
    border-left: 3px solid #c0392b;
}

.print-quote-modal .convert-quote-row .custom-control-label {
    cursor: pointer;
    width: 100%;
}

.print-quote-modal .convert-quote-row-main {
    display: flex;
    align-items: baseline;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 2px;
}

.print-quote-modal .convert-quote-row-label {
    font-size: 14px;
    font-weight: 700;
    color: #c0392b;
    letter-spacing: 0.3px;
}

.print-quote-modal .convert-quote-row-meta {
    font-size: 12px;
    color: #555;
    font-weight: 400;
}

.print-quote-modal .convert-quote-row-meta strong {
    color: #222;
    font-weight: 700;
}

.print-quote-modal .convert-quote-row-sub {
    font-size: 11px;
    color: #888;
    letter-spacing: 0.2px;
    text-transform: uppercase;
}

.print-quote-modal .convert-quote-row-amount {
    color: #c0392b;
    font-weight: 700;
}

/* Per-assembly mini-list inside each row — one line per
   (machine, assembly, part-count) tuple, indented and
   smaller so it reads as supplementary detail beneath the
   group's primary label. Mirrors the layout used by the
   Finalize modal on /cart/rfqsaveditems. */
.print-quote-modal .convert-quote-row-assemblies {
    margin-top: 4px;
    font-size: 11px;
    color: #666;
    line-height: 1.4;
}

.print-quote-modal .convert-quote-row-assembly {
    padding: 1px 0;
}

/* Single-quote case: no checkbox, just the same row
   layout as the multi-quote case minus the form control.
   .print-quote-row-static is the wrapper that replaces
   b-form-checkbox in that branch. */
.print-quote-modal .print-quote-row-static {
    padding: 0;
}

/* Select All / Deselect All link, right-aligned beneath
   the row list. Brand red, small caps treatment to match
   the Finalize modal. */
.print-quote-modal .convert-quote-toggle-all {
    text-align: right;
    margin: 0 0 4px;
}

.print-quote-modal .convert-quote-toggle-all a {
    font-size: 11px;
    font-weight: 700;
    color: #c0392b;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    cursor: pointer;
    text-decoration: none;
}

.print-quote-modal .convert-quote-toggle-all a:hover {
    text-decoration: underline;
}
</style>

<!-- ============================================================
     PRINT STYLESHEET (non-scoped, global @media print)
     ============================================================
     Same pattern as OrderDetails.vue. Hides page chrome,
     Convert / Delete buttons, Print band. Reveals the
     print-only letterhead header. Forces every quote
     assembly's parts table expanded regardless of the
     user's expand/collapse state, so the printed receipt
     contains all line items. -->
<style>
@media print {
    /* Hide chrome — multiple selector forms with elevated
       specificity to win against any global stylesheet
       rules with !important on these elements. */
    body #breadcrumb,
    body #breadcrumb *,
    div#breadcrumb,
    div#breadcrumb *,
    [id="breadcrumb"],
    [id="breadcrumb"] *,
    [class*="breadcrumb" i],
    [class*="breadcrumb" i] *,
    [id*="breadcrumb" i],
    [id*="breadcrumb" i] *,
    nav[aria-label*="breadcrumb" i],
    nav[aria-label*="Breadcrumb"],
    ol.breadcrumb,
    ul.breadcrumb,
    body header,
    body .header,
    body #header,
    body [role="banner"],
    body nav.navbar,
    body .navbar,
    body footer,
    body .footer,
    body #footer,
    /* Hide the on-screen page-header pieces (Quote # + Date + action
       buttons, Special Instructions) — the per-quote print letterhead
       carries the equivalent info in print. These used to be hidden via
       `body #odTop`, but #OrderTable now lives INSIDE #odTop's content
       card (single-card consolidation), so hiding the whole #odTop would
       blank the printed quote. Target the header blocks specifically and
       leave #OrderTable visible. (The action buttons are also .no-print,
       so they're covered either way.) */
    body #odTop > .container > .order-page-header,
    body #odTop > .container > .special-instructions-block,
    .no-print,
    [class*="userway"],
    [id*="userway"],
    [class*="accessibility-widget"],
    [class*="translate-widget"],
    [id*="google_translate"],
    iframe {
        display: none !important;
        visibility: hidden !important;
        height: 0 !important;
        overflow: hidden !important;
        margin: 0 !important;
        padding: 0 !important;
    }

    /* Strip the brushed-steel body background. */
    html,
    body {
        background: #fff !important;
        background-image: none !important;
    }

    /* ===== STRIP TOP-OF-PAGE WHITESPACE =====
       App.vue sets a dynamic padding-top on body to clear
       the fixed site header. That padding has no purpose
       in print (header is hidden) but the inline style
       stays and creates a tall blank band at page top.
       Force it to zero. */
    html,
    body,
    #app,
    #app > div,
    main,
    .main,
    [role="main"],
    .container-fluid,
    #orderDetails {
        margin-top: 0 !important;
        padding-top: 0 !important;
    }
    body[style] {
        padding-top: 0 !important;
        margin-top: 0 !important;
    }

    /* ===== PRINTABLE RECEIPT SPANS THE FULL PAGE =====
       #OrderTable (tagged .jcs-print-doc, the print-only root) renders inside
       #odTop's content card after the single-card consolidation. That card is
       a Bootstrap .container, whose max-width (540/720/960px at the print
       breakpoint) squeezed the receipt into a narrow, left-aligned column
       once it moved inside. Neutralize the width + box chrome on the WHOLE
       ancestor chain so the printout owns the full @page width and is
       INDEPENDENT of however the on-screen layout nests or restyles
       .container — the receipt is anchored to its own .jcs-print-doc root,
       not the shared site container. (The ::before brand stripe that drew the
       card's red line is killed just below.) */
    #orderDetails,
    #orderDetails #odTop,
    #orderDetails #odTop > .container,
    #orderDetails .jcs-print-doc {
        width: 100% !important;
        max-width: 100% !important;
        flex: 0 0 100% !important;
        margin: 0 !important;
        padding: 0 !important;
        background: transparent !important;
        border: 0 !important;
        box-shadow: none !important;
        border-radius: 0 !important;
    }
    #orderDetails #odTop > .container::before {
        display: none !important;
        content: none !important;
    }

    /* Show the print-only header. Hidden on screen by the
       scoped rule above; revealed in print here. */
    .print-only-header {
        display: flex !important;
        align-items: flex-start;
        justify-content: space-between;
        padding: 0 5px 16px;
        margin: 0 0 16px;
        border-bottom: 2px solid #c0392b;
    }

    .print-only-header-logo {
        flex: 0 0 auto;
        margin-left: 5px;
    }

    .print-only-header-logo-img {
        width: 180px;
        max-width: 180px;
        height: auto;
        max-height: 80px;
        display: block;
    }

    /* Text fallback used when the SVG <img> fails to load. */
    .print-only-header-logo-text {
        font-size: 32px;
        font-weight: 900;
        letter-spacing: 1px;
        color: #c0392b;
        line-height: 1;
        font-family: Arial, Helvetica, sans-serif;
    }

    .print-only-header-customer {
        flex: 1 1 auto;
        align-self: center;
        text-align: center;
        padding: 0 16px;
        color: #222;
        min-width: 0;
    }
    .print-only-header-customer-name {
        font-weight: 700 !important;
        font-size: 16px;
        line-height: 1.2;
    }
    .print-only-header-customer-id {
        font-size: 13px;
        color: #555;
        margin-top: 2px;
    }
    .print-only-header-address {
        flex: 0 0 auto;
        text-align: right;
        font-size: 12px;
        line-height: 1.4;
        color: #222;
    }

    .print-only-header-address-name {
        font-weight: 700;
        font-size: 12px;
        margin-bottom: 2px;
    }

    /* Doc identifier line above the Parts Department block.
       Brand-red + larger weight so it reads as the headline
       of the printed page — recipient can tell at a glance
       which Order/Quote/Acknowledgement they're looking at.
       Color-adjust hint forces the red through browsers'
       default print color-strip (which the rest of this
       stylesheet already uses on red elements). */
    .print-only-header-doc-id {
        font-weight: 700;
        font-size: 14px;
        color: #c0392b;
        margin-bottom: 6px;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
    }

    /* ===== INPUT-AS-TEXT IN PRINT =====
       Disabled <input> elements (e.g. the locked CART QTY
       input on Success.vue's parts table) carry intrinsic
       browser-default widths that override table-cell
       sizing and push other columns out of alignment with
       the header row. Strip the input chrome and intrinsic
       width so the value renders inline as text — same
       width as the other plain-text cells in the column.
       Defensive on files that don't currently use inputs in
       tables; harmless if no inputs are present. */
    .assembly-table-wrap input[disabled],
    .assembly-table-wrap input[readonly],
    .POOrders input[disabled],
    .POOrders input[readonly],
    .success-qty,
    .success-qty input {
        border: 0 !important;
        background: transparent !important;
        padding: 0 !important;
        margin: 0 !important;
        width: auto !important;
        max-width: none !important;
        min-width: 0 !important;
        display: inline !important;
        -webkit-appearance: none !important;
        appearance: none !important;
        color: inherit !important;
        font: inherit !important;
        box-shadow: none !important;
        outline: 0 !important;
    }

    /* ===== TIGHT TYPOGRAPHY FOR PRINT =====
       Same density as OrderDetails so a quote fits in as
       few pages as possible. */
    body {
        font-size: 12px !important;
        line-height: 1.4 !important;
    }
    #orderDetails h1 {
        font-size: 20px !important;
        margin: 0 0 4px !important;
    }
    #orderDetails h2 {
        font-size: 15px !important;
        margin: 0 0 4px !important;
    }
    #orderDetails p {
        font-size: 12px !important;
        line-height: 1.4 !important;
        margin: 0 0 2px !important;
    }
    #odTop {
        padding-top: 8px !important;
        padding-bottom: 8px !important;
    }
    #odTop .container {
        padding-top: 0 !important;
        padding-bottom: 0 !important;
    }
    #OrderTable .container {
        padding-top: 4px !important;
        padding-bottom: 4px !important;
    }
    .order-page-header {
        margin-bottom: 6px !important;
    }
    .order-page-header-meta {
        font-size: 12px !important;
    }
    .po-group {
        margin-top: 6px !important;
        margin-bottom: 6px !important;
        padding: 6px 8px !important;
    }
    .order-header {
        margin-bottom: 6px !important;
        padding-bottom: 6px !important;
        display: flex !important;
        align-items: center !important;
        justify-content: space-between !important;
    }
    /* PO # / Quote reference as a black rectangle chip with white text. */
    .order-header-label {
        font-size: 16px !important;
        font-weight: 700 !important;
        background: #000 !important;
        color: #fff !important;
        padding: 4px 12px !important;
        border-radius: 3px !important;
        letter-spacing: 0.3px !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
    }
    /* Item count on the right of the same row. */
    .order-header-meta {
        font-size: 14px !important;
        font-style: normal !important;
        color: #000 !important;
    }
    .assembly-card {
        margin-bottom: 4px !important;
        padding: 6px 8px !important;
    }
    .assemblyName {
        font-size: 14px !important;
        margin: 0 !important;
    }
    .assemblyDescription {
        font-size: 11px !important;
        margin: 0 !important;
    }
    .assembly-card-po-label {
        font-size: 11px !important;
    }
    .assembly-card-po-amount {
        font-size: 12px !important;
    }
    .assembly-summary {
        /* Redundant in print — the full parts table is force-expanded
           below, so hide the collapsed "X parts + chips" preview row. */
        display: none !important;
    }
    /* Per-assembly "Subtotal for Quote Reference: X" row — removed from
       print per request (the grand total + per-part costs already cover it). */
    .assembly-card-po-subtotal {
        display: none !important;
    }
    /* Per-table totals line below the table (NOT a table row): plain,
       right-aligned "Total: <qty> at <cost>" — bold label, red amount. */
    .parts-total-line {
        display: block !important;
        text-align: right !important;
        margin: 4px 2px 0 !important;
        font-size: 13px !important;
    }
    .parts-total-strong {
        font-weight: 700 !important;
    }
    .parts-total-amount {
        color: #c0392b !important;
        font-weight: 700 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
    }
    /* Quote Total value → bold red (force the red through the browser's
       default print color-strip). */
    .cart-grand-total-value {
        color: #c0392b !important;
        font-weight: 700 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
    }
    .assembly-summary-count,
    .code-chip,
    .code-qty {
        font-size: 9px !important;
    }
    .assembly-summary-total {
        font-size: 10px !important;
    }
    .assembly-table-wrap {
        margin: 4px 0 0 !important;
    }
    .assembly-table-wrap table {
        font-size: 12px !important;
        /* Full content width + FIXED layout: under auto-layout the header
           cells and data cells drifted out of alignment and the columns
           squished into the left ~60%. Fixed layout makes every row share
           the same column widths (so headers line up with their values) and
           the table fills the page. */
        width: 100% !important;
        table-layout: fixed !important;
    }
    .assembly-table-wrap th,
    .assembly-table-wrap td {
        padding: 5px 8px !important;
        font-size: 12px !important;
        white-space: normal !important;
        word-break: break-word;
        vertical-align: top;
        overflow: hidden;
    }
    /* Value cells (Price / Quoted / Cost etc.) in pure black — they
       inherited a washed-out gray body color. Chips/badges keep their own
       colors because those are set on inner spans. */
    .assembly-table-wrap td {
        color: #000 !important;
    }
    /* Description (3rd column) takes the bulk of the width; the six short
       columns split the remainder evenly under fixed layout. */
    .assembly-table-wrap th:nth-child(3),
    .assembly-table-wrap td:nth-child(3) {
        width: 34% !important;
    }
    .cart-grand-total {
        margin-top: 8px !important;
        padding: 8px 12px !important;
    }
    .cart-grand-total-label {
        font-size: 14px !important;
        font-weight: 700 !important;
        color: #000 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
    }
    /* Show the "N Parts at" prefix in print (hidden on screen). It sits
       inside .cart-grand-total-value so it inherits the red + bold. */
    .grand-total-parts-prefix {
        display: inline !important;
        color: #c0392b !important;
        font-weight: 700 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
    }
    .cart-grand-total-value {
        font-size: 14px !important;
    }
    .special-instructions-block {
        padding: 6px 8px !important;
        margin: 4px 0 !important;
        font-size: 9px !important;
    }
    .special-instructions-label {
        font-size: 9px !important;
    }
    .special-instructions-text {
        font-size: 9px !important;
    }

    /* Page break control — only on small atoms, not on the
       wide po-group container (which can force whole-block
       page-shoves and leave white gaps). */
    .info-card,
    .cart-grand-total,
    .assembly-card {
        page-break-inside: avoid;
        break-inside: avoid;
    }

    /* ===== FORCE-EXPAND COLLAPSED ASSEMBLIES =====
       v-show writes inline display:none when collapsed;
       override with !important to show every parts table
       in print regardless of expand/collapse state. */
    .assembly-table-wrap {
        display: block !important;
    }
    .assembly-table-wrap[style*="display: none"],
    .assembly-table-wrap[style*="display:none"] {
        display: block !important;
    }
    /* Hide the +/− toggle in print since the section
       it controls is always visible now. */
    .assembly-toggle {
        display: none !important;
    }

    /* b-table stacked-mode override — force back to row
       layout for print (print viewport trips the lg
       breakpoint and would stack each cell vertically). */
    .b-table.b-table-stacked-lg > tbody > tr,
    .b-table.b-table-stacked-md > tbody > tr,
    .b-table.b-table-stacked-sm > tbody > tr {
        display: table-row !important;
    }
    .b-table.b-table-stacked-lg > tbody > tr > td,
    .b-table.b-table-stacked-md > tbody > tr > td,
    .b-table.b-table-stacked-sm > tbody > tr > td {
        display: table-cell !important;
    }
    .b-table.b-table-stacked-lg > thead,
    .b-table.b-table-stacked-md > thead,
    .b-table.b-table-stacked-sm > thead {
        display: table-header-group !important;
    }
    .b-table.b-table-stacked-lg > tbody > tr > [data-label]::before,
    .b-table.b-table-stacked-md > tbody > tr > [data-label]::before,
    .b-table.b-table-stacked-sm > tbody > tr > [data-label]::before {
        display: none !important;
    }

    /* ===== PER-QUOTE PAGE BREAKS =====
       Each .print-quote-section past the first gets a
       page-break-before so every Quote # block starts on
       its own printed page with its own letterhead at the
       top. The first section inherits the page-top
       position naturally — no break needed there. */
    .print-quote-section + .print-quote-section {
        page-break-before: always;
        break-before: page;
    }

    /* Also keep each section internally cohesive — avoid
       the rare case where a group splits awkwardly across
       a page boundary mid-assembly. Inner .assembly-card
       already has page-break-inside: avoid above for the
       same reason at a finer grain. */
    .print-quote-section {
        page-break-inside: auto;
        break-inside: auto;
    }

    /* ===== SELECTIVE HIDE FOR SUBSET PRINTING =====
       confirmPrint() flips printingSelectedOnly=true and
       Vue toggles `print-quote-hidden` onto every section
       whose quote.number isn't in selectedQuotesForPrint.
       The display: none here removes those sections from
       the printed output entirely — including their
       per-quote letterhead — so a 3-of-5 print only emits
       the picked 3 with correct page breaks between them.
       The companion `+` selector above already handles
       page-break-before on whichever section ends up
       second in the printed sequence. */
    .print-quote-hidden,
    .print-quote-hidden * {
        display: none !important;
        visibility: hidden !important;
        height: 0 !important;
        overflow: hidden !important;
        margin: 0 !important;
        padding: 0 !important;
    }

    @page {
        margin: 0.5in;
    }

    /* ===== FORCE DARK TEXT FOR PRINT READABILITY =====
       Browser print engines lighten text by default to save
       ink. Combined with the various screen-only light-gray
       colors (#555, #666, #777, #888, #aaa) sprinkled across
       the screen styles, the printed output ends up muted
       and hard to read. Force every text node to full black
       AND bump font-weight slightly so thin glyphs don't
       disappear at small print font sizes. */
    *,
    *::before,
    *::after {
        color: #000 !important;
    }

    body,
    body p,
    body span,
    body td,
    body th,
    body div {
        font-weight: 500 !important;
    }

    /* ===== AGGRESSIVE GLOBAL DECORATIVE RESET =====
       Earlier attempts to strip borders only on specific
       containers didn't eliminate the vertical gray lines
       running down the body of printed pages. Edge browser
       in particular prints subtle borders by default —
       different browsers behave differently here.

       Brute-force approach: wipe ALL backgrounds, borders,
       and shadows on EVERY element AND every pseudo-element.
       The pseudo-element coverage matters — App.vue (or a
       global stylesheet) sets up `body::before` and
       `body::after` to render the brushed-steel side rails
       around the page, and a universal `*` selector by
       itself doesn't catch those. Without this rule the
       rails stay visible in print and produce vertical bars
       running the full page height.

       border-color: transparent (rather than border-width:
       0) preserves layout — elements that used borders for
       spacing keep their box geometry, only the visible
       line goes away. */
    *,
    *::before,
    *::after {
        background: transparent !important;
        background-color: transparent !important;
        background-image: none !important;
        border-color: transparent !important;
        box-shadow: none !important;
        outline: none !important;
    }

    /* Explicit body pseudo-element kill — App.vue's brushed-
       steel side rails attach here. Belt-and-suspenders with
       the universal rule above; some browsers need the
       targeted nudge to actually drop the rendered content. */
    body::before,
    body::after,
    html::before,
    html::after {
        display: none !important;
        content: none !important;
    }

    /* ===== HIDE FLOATING UTILITY TOOLBAR =====
       Head.vue mounts a fixed-position #utility-toggle-stack
       in the bottom-right of every page, containing three
       toggles: Report-a-Bug, font-size (Aa), and translate
       (globe). These are screen-only UI affordances; in a
       printed receipt they're noise. */
    #utility-toggle-stack,
    #utility-toggle-stack *,
    .util-toggle,
    .util-toggle-bug,
    .util-toggle-font,
    .util-toggle-translate,
    .util-icon,
    .util-label,
    .util-close,
    .util-body,
    #google-translate-floating,
    #google_translate_element {
        display: none !important;
        visibility: hidden !important;
        height: 0 !important;
        width: 0 !important;
        overflow: hidden !important;
    }

    /* Hide any horizontal-rule elements which would otherwise
       print as visible divider lines. */
    hr {
        display: none !important;
    }

    /* ===== RESTORE INTENTIONAL CHROME =====
       Now re-establish the visual elements that DO belong
       in print:
         - Data table cell borders carry data structure
         - print-only-header gets its red bottom rule (the
           branded divider between header and content)
         - Red brand accents stay red */
    table,
    .assembly-table-wrap table {
        border-collapse: collapse !important;
    }
    th,
    td,
    .assembly-table-wrap th,
    .assembly-table-wrap td {
        border: 1px solid #888 !important;
    }

    .print-only-header {
        border-bottom: 2px solid #c0392b !important;
    }

    /* Targeted color preservation on the red brand elements
       so they print in their declared color instead of the
       browser's default auto-strip. */
    .print-only-header,
    .print-only-header *,
    .print-only-header-logo-img,
    .print-only-header-logo-text,
    .print-only-header-logo-text *,
    [class*="-red"],
    [class*="brand-red"] {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
    }

    /* Restore red on the brand wordmark fallback + any
       explicit red accents the page intentionally carries.
       Order matters — these rules must come after the
       universal `*` color override above. */
    .print-only-header-logo-text,
    .print-only-header-logo-text *,
    [class*="-red"],
    [class*="brand-red"] {
        color: #c0392b !important;
    }
}
</style>